/**
 * Tiltímetro por eixo — GUPM-270. TS puro (sem React, sem Supabase, sem DOM).
 *
 * O tiltímetro mede duas inclinações independentes (eixo A e eixo B) e a
 * temperatura do sensor, tudo no MESMO timestamp. Isso quebra duas premissas que
 * o app tinha: "uma leitura atual por instrumento" e "um conjunto de limiares por
 * linha de tabela". Este módulo é o lugar onde as três leituras viram uma linha.
 *
 * A régua da Carta é em MÓDULO (|x| ≥ 5° → Atenção, ≥ 7,5° → Alerta, ≥ 15° →
 * Emergência) e a leitura é assinada. Em vez de classificar com `Math.abs` — o que
 * exigiria um caminho de classificação só para tilt, no TS e no SQL — as bandas
 * são montadas SIMÉTRICAS e ANINHADAS. A regra que já existe nos dois lados
 * ("vence a banda menos severa que contém o valor") então resolve os dois sentidos
 * sozinha: +6 e −6 caem ambos na banda de atenção; ±16 não cai em nenhuma e vira
 * o pior caso.
 */
import type {
  Canal, Instrument, InstrumentThreshold, InstrumentType, Reading, TarpLevel,
} from '../types';
import { asInstrumentId } from '../types';
import {
  nivelDeInstrumentoMultiCanal, semSinalVisivel, type NivelExibido,
} from './nivel-exibido';
import { foraDeServico } from './fora-de-servico';

/** Faixa vigente de um canal, na forma que a view `v_faixas_canal` entrega. */
export interface FaixaCanal {
  instrumentId: string;
  canal: Canal;
  validFrom: Date;
  /** Módulo em que começa cada nível (banda simétrica: vale para os dois sentidos). */
  inicioAtencao: number;
  inicioAlerta: number;
  inicioEmergencia: number;
}

/** Limiares em módulo exibidos na tabela (um conjunto serve os dois eixos). */
export interface LimitesTilt {
  atencao: number;
  alerta: number;
  emergencia: number;
}

/** Leitura atual de um eixo, com o nível do próprio eixo. */
export interface EixoTilt {
  value: number;
  level: TarpLevel | null;
}

/** Uma linha da tabela de tiltímetros: um instrumento, dois eixos. */
export interface LinhaTiltimetro {
  instrument: Instrument;
  eixoA: EixoTilt | null;
  eixoB: EixoTilt | null;
  temperatura: number | null;
  /** Pior eixo com faixa (ou estado sem lastro). */
  nivel: NivelExibido;
  /** `null` quando os eixos não compartilham a mesma faixa. */
  limites: LimitesTilt | null;
}

/** Os dois canais que a Carta classifica num tiltímetro. A temperatura não tem régua. */
export const CANAIS_TILT: readonly Canal[] = ['eixo_a', 'eixo_b'];

/**
 * Tipos cuja grandeza é BILATERAL e cuja régua mora em `instrument_thresholds` por
 * canal — hoje só o tiltímetro.
 *
 * Existe para negar o caminho legado: quem é bilateral **nunca** pode cair nos campos
 * escalares `level_*_max` do instrumento. Eles são resíduo de seed, e o resíduo não é
 * hipotético — o TIL-02 da Barragem B carrega 1/2/3 ° neles em produção (carta
 * fabricada, GUPM-225, com zero linha em `instrument_thresholds`). Usá-los desenharia
 * uma régua unilateral, sem Emergência e num módulo 2,5× menor que o real.
 */
export const TIPOS_BILATERAIS: readonly InstrumentType[] = ['TILT'];

/** `true` para instrumento de grandeza bilateral (régua em módulo, carta por canal). */
export function ehBilateral(type: InstrumentType): boolean {
  return TIPOS_BILATERAIS.includes(type);
}

/**
 * Menor passo representável em `numeric(12,4)` — a precisão de `readings.value` e
 * das colunas de `instrument_thresholds`.
 *
 * Serve para tornar o limite INFERIOR de cada banda exclusivo. A classificação usa
 * `[min, max)`: conservadora no topo (5,0 exatos já sobem de nível) e **inclusiva
 * embaixo**, onde −5,0 cairia dentro de Normal. Numa régua em módulo isso está
 * errado por um nível inteiro em cada borda negativa: −5 → Normal (devia ser
 * Atenção), −7,5 → Atenção (devia ser Alerta), −15 → Alerta (devia ser
 * Emergência). Deslocar o mínimo um passo para cima faz a borda negativa pertencer
 * à banda mais severa, simétrico ao que já acontece no topo.
 *
 * Não é aproximação: com 4 casas decimais no banco, não existe valor entre −5,0000
 * e −4,9999. E é exatamente a notação que a própria Carta usa nas bandas
 * bilaterais dos marcos, onde o normal é aberto embaixo (`-10,22 < Leitura`).
 */
const PASSO_NUMERIC_4 = 0.0001;

/** Limite inferior exclusivo de uma banda simétrica de módulo `m`. */
function minExclusivo(m: number): number {
  return -m + PASSO_NUMERIC_4;
}

/**
 * Monta a faixa simétrica aninhada a partir dos três módulos da Carta.
 *
 * `normal ⊂ atenção ⊂ alerta`, emergência ilimitada — a classificação devolve a
 * banda menos severa que contém o valor, então +6 e −6 caem os dois em Atenção e
 * ±16 não cai em nenhuma, virando o pior caso. As bordas são conservadoras nos
 * DOIS sentidos: no topo pela convenção `[min, max)`, embaixo por `minExclusivo`.
 */
export function faixaSimetricaTilt(i: {
  instrumentId: string;
  canal: Canal;
  validFrom: Date;
  atencao: number;
  alerta: number;
  emergencia: number;
}): InstrumentThreshold {
  return {
    instrumentId: asInstrumentId(i.instrumentId),
    canal: i.canal,
    validFrom: i.validFrom,
    normal: { min: minExclusivo(i.atencao), max: i.atencao },
    atencao: { min: minExclusivo(i.alerta), max: i.alerta },
    alerta: { min: minExclusivo(i.emergencia), max: i.emergencia },
    // Sem limite: qualquer valor fora das bandas acima é emergência.
    emergencia: { min: null, max: null },
  };
}

/** Faixa → os três módulos exibidos na tabela (começo de cada nível). */
function limitesDaFaixa(f: FaixaCanal): LimitesTilt {
  return { atencao: f.inicioAtencao, alerta: f.inicioAlerta, emergencia: f.inicioEmergencia };
}

function mesmosLimites(a: LimitesTilt, b: LimitesTilt): boolean {
  return a.atencao === b.atencao && a.alerta === b.alerta && a.emergencia === b.emergencia;
}

/**
 * Limiares a exibir na linha. `null` quando os dois eixos NÃO compartilham a mesma
 * faixa — a tabela tem um conjunto único de colunas, e mostrar ali os números do
 * eixo A ao lado do valor do eixo B convidaria a comparação errada numa tela de
 * segurança de barragem. Hoje a carga grava faixas iguais nos dois eixos; a Carta
 * real, quando chegar, pode trazer faixa por eixo (é o que ela faz nos marcos).
 */
function limitesDaLinha(fa: FaixaCanal | undefined, fb: FaixaCanal | undefined): LimitesTilt | null {
  const la = fa ? limitesDaFaixa(fa) : null;
  const lb = fb ? limitesDaFaixa(fb) : null;
  if (la && lb) return mesmosLimites(la, lb) ? la : null;
  return la ?? lb;
}

/**
 * Pivô: leituras atuais POR CANAL → uma linha por tiltímetro.
 *
 * `leiturasAtuais` é o conjunto de `readings_current` (já uma linha por canal).
 * Leitura de canal sem faixa entra na linha como dado exibível (a temperatura),
 * mas não participa do nível — quem decide o semáforo é `nivelDeInstrumentoMultiCanal`.
 */
export function linhasDeTiltimetro(
  tiltimetros: readonly Instrument[],
  leiturasAtuais: readonly Reading[],
  faixas: readonly FaixaCanal[],
): LinhaTiltimetro[] {
  return tiltimetros.map((instrument) => {
    const doInstrumento = leiturasAtuais.filter((r) => r.instrumentId === instrument.id);
    const porCanal = (canal: Canal): Reading | undefined =>
      doInstrumento.find((r) => r.canal === canal);

    const eixo = (canal: Canal): EixoTilt | null => {
      const r = porCanal(canal);
      return r ? { value: r.value, level: r.level } : null;
    };

    const faixasDo = faixas.filter((f) => f.instrumentId === String(instrument.id));
    const canaisComFaixa = faixasDo.map((f) => f.canal);

    // GUPM-303 — instrumento mudo não exibe valor NENHUM dos três. Eixo A, eixo B e
    // temperatura chegam no MESMO registro da v2, com o mesmo `dataHoraLeitura`: se ele
    // parou de publicar, os três envelheceram juntos. Apagar os eixos e deixar a
    // temperatura afirmaria que aquele número é atual — e a linha ficaria dizendo "Sem
    // sinal" ao lado de um dado que parece fresco.
    //
    // Os LIMIARES não entram nesta regra: vêm da Carta de Controle, são cadastro e
    // seguem válidos com o sensor mudo. Quem quiser saber em que condição o sensor
    // parou tem a série inteira no M6.
    const mudo = semSinalVisivel(instrument);

    return {
      instrument,
      eixoA: mudo ? null : eixo('eixo_a'),
      eixoB: mudo ? null : eixo('eixo_b'),
      temperatura: mudo ? null : (porCanal('temperatura')?.value ?? null),
      // Uma única implementação da regra "pior canal com faixa" — a mesma que o
      // mapa, o dashboard e o M6 usam. Duplicá-la aqui daria duas respostas
      // possíveis para o semáforo do mesmo instrumento.
      // GUPM-303 — a tabela de tiltimetros e justamente a tela do instrumento que a
      // perda de sinal existe para consertar: se ela nao passasse o frescor, ficaria
      // verde enquanto Dashboard e Mapa dizem "sem sinal".
      // GUPM-307 — e declarado fora de servico vence o mudo: o silencio e conhecido.
      nivel: nivelDeInstrumentoMultiCanal(
        doInstrumento, canaisComFaixa, mudo, foraDeServico(instrument),
      ),
      limites: limitesDaLinha(
        faixasDo.find((f) => f.canal === 'eixo_a'),
        faixasDo.find((f) => f.canal === 'eixo_b'),
      ),
    };
  });
}

